import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApi } from "../useApi.js";
import { api, nf, dateDe, dateTimeDe } from "../api.js";
import type { CrewRow, DeviceDetail as DD, Me } from "../types.js";
import { Page, Pill, statusLabel } from "../components/Shell.js";
import WearRing from "../components/WearRing.js";
import { toast } from "../toast.js";

const TABS = [
  ["wear", "Zustand & Verschleiß"], ["prot", "Einblasprotokolle"],
  ["hist", "Historie"], ["base", "Stammdaten"],
] as const;
const unitLabel = (u: string) => (u === "HOUR" ? "Bh" : "m");

export default function DeviceDetail({ me }: { me: Me }) {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<"wear" | "prot" | "hist" | "base">("wear");
  const [busy, setBusy] = useState<string | null>(null);
  const [crewBusy, setCrewBusy] = useState(false);
  const [crewError, setCrewError] = useState<string | null>(null);
  const { data: d, error, loading, reload } = useApi<DD>(id ? `/api/devices/${id}` : null);
  const { data: crews } = useApi<CrewRow[]>("/api/crews");

  async function changeCrew(crew: string) {
    setCrewBusy(true);
    setCrewError(null);
    try {
      await api.patch(`/api/devices/${id}`, { crew });
      await reload();
      toast("Kolonne geändert", "success");
    } catch (e) {
      setCrewError(e instanceof Error ? e.message : "Kolonne konnte nicht geändert werden");
    } finally {
      setCrewBusy(false);
    }
  }

  async function resetWear(partId: string) {
    setBusy(partId);
    try {
      await api.post(`/api/devices/${id}/wear/${partId}/reset`);
      await reload();
      toast("Teiletausch quittiert", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Fehlgeschlagen", "error");
    } finally { setBusy(null); }
  }

  if (loading) return <Page title="Gerät"><div className="empty">Wird geladen…</div></Page>;
  if (error || !d) return <Page title="Gerät"><div className="empty">{error ?? "Nicht gefunden"}</div></Page>;

  return (
    <Page title={d.inventoryNo} sub={`${d.crew} · ${d.location}`}
          actions={<Link className="btn ghost" to="/geraete">Zur Liste</Link>}>

      <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 18 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span className="mono" style={{ fontFamily: "var(--f-display)", fontSize: 25, fontWeight: 600 }}>
              {d.inventoryNo}
            </span>
            <Pill status={d.status}>{statusLabel(d.status)}</Pill>
            <span className="pill mute mono">FW {d.firmware}</span>
          </div>
          {d.reasons.length > 0 && (
            <div style={{ marginTop: 9, color: "var(--ink-2)", fontSize: 13 }}>{d.reasons.join(" · ")}</div>
          )}
        </div>
        <div className="kpis" style={{ flex: "none", minWidth: "min(100%, 470px)" }}>
          <div className="kpi"><span className="v num">{nf(d.totalMeters / 1000, 1)}</span>
            <span className="l">Tsd. Einblasmeter</span><span className="m">{nf(d.runCount)} Vorgänge</span></div>
          <div className="kpi"><span className="v num">{nf(d.totalHours)}</span>
            <span className="l">Betriebsstunden</span><span className="m">Ø {nf(d.metersPerDay)} m/Tag</span></div>
          <div className="kpi"><span className="v num">{d.protocols.length}</span>
            <span className="l">Protokolle</span><span className="m">zuletzt übernommen</span></div>
        </div>
      </div>

      <div className="seg" style={{ marginBottom: 16 }}>
        {TABS.map(([k, l]) => (
          <button key={k} aria-pressed={tab === k} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === "wear" && (
        <div className="card">
          <div className="card-h">
            <h3>Verschleißzähler</h3>
            <span className="hint">Zählerstand seit letztem Wechsel, fortgeschrieben aus den Einblasprotokollen</span>
          </div>
          <div className="tbl-wrap"><table>
            <thead><tr>
              <th>Verschleißteil</th><th>Anwendung</th><th className="r">Zählerstand</th>
              <th style={{ minWidth: 180 }}>Verbraucht</th><th className="r">Restlaufzeit</th><th>Status</th><th />
            </tr></thead>
            <tbody>
              {d.wear.map((w) => (
                <tr key={w.partId}>
                  <td><b>{w.name}</b>
                    <div className="sub-t mono">Pos. {w.sku} · Intervall {nf(w.lifeValue)} {unitLabel(w.unit)}</div></td>
                  <td className="sub-t">{w.group}</td>
                  <td className="r num">{nf(w.since)} {unitLabel(w.unit)}
                    <div className="sub-t num">seit {dateDe(w.changedAt)}</div></td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      <WearRing fraction={w.fraction} status={w.status} size={26} />
                      <span className="num" style={{ fontSize: 11.5, color: "var(--ink-2)", width: 38, textAlign: "right" }}>
                        {Math.round(w.fraction * 100)} %
                      </span>
                    </div>
                  </td>
                  <td className="r num">
                    {w.status === "CRITICAL" ? "überschritten" : `${nf(w.remaining)} ${unitLabel(w.unit)}`}
                    <div className="sub-t">{w.status === "CRITICAL" ? "sofort wechseln" : `≈ ${w.remainingDays} Tage`}</div>
                  </td>
                  <td><Pill status={w.status}>
                    {w.status === "CRITICAL" ? "Fällig" : w.status === "WARNING" ? "Bald fällig" : "In Ordnung"}
                  </Pill></td>
                  <td className="r">
                    <button className="btn sm" disabled={busy === w.partId}
                            onClick={() => void resetWear(w.partId)}>
                      {busy === w.partId ? "…" : "Gewechselt"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {tab === "prot" && (
        <div className="card">
          <div className="card-h"><h3>Übernommene Einblasprotokolle</h3>
            <span className="hint">Das Protokoll entsteht auf dem Gerät; Ductus liest es ein.</span></div>
          <div className="tbl-wrap">
            {d.protocols.length === 0
              ? <div className="empty">Noch kein Protokoll übernommen.</div>
              : <table>
                  <thead><tr><th>Datum</th><th>Auftrag</th><th>Rohr / Kabel</th>
                    <th className="r">Länge</th><th className="r">F max</th><th className="r">p max</th></tr></thead>
                  <tbody>
                    {d.protocols.map((p) => (
                      <tr key={p.id}>
                        <td className="num">{dateDe(p.startedAt)}</td>
                        <td className="mono" style={{ fontSize: 12 }}>{p.orderNo}</td>
                        <td>{p.ductType}<div className="sub-t">{p.cableType}</div></td>
                        <td className="r num">{nf(p.lengthM)} m</td>
                        <td className="r num">{nf(p.maxForceN, 1)} N</td>
                        <td className="r num">{nf(p.maxPressureBar, 1)} bar</td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </div>
        </div>
      )}

      {tab === "hist" && (
        <div className="card">
          <div className="card-h"><h3>Historie</h3>
            <span className="hint">Teiletausch und Servicebesuche dieses Geräts, neueste zuerst</span></div>
          <div className="tbl-wrap">
            {d.history.length === 0
              ? <div className="empty">Noch kein Eintrag.</div>
              : <table>
                  <thead><tr><th>Datum</th><th>Art</th><th>Ereignis</th><th>Benutzer</th></tr></thead>
                  <tbody>
                    {d.history.map((h, i) => (
                      <tr key={i}>
                        <td className="num" style={{ whiteSpace: "nowrap" }}>{dateTimeDe(h.at)}</td>
                        <td><span className={`pill ${h.kind === "wear" ? "info" : "mute"}`}>
                          {h.kind === "wear" ? "Teiletausch" : "Service"}
                        </span></td>
                        <td>{h.text}</td>
                        <td className="sub-t">{h.actor ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </div>
        </div>
      )}

      {tab === "base" && (
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(290px,1fr))" }}>
          <div className="card"><div className="card-h"><h3>Gerät</h3></div><div className="card-b">
            <dl className="dl">
              <dt>Inventarnummer</dt><dd>{d.inventoryNo}</dd>
              <dt>Seriennummer</dt><dd>{d.serialNo}</dd>
              <dt>Modell</dt><dd>{d.model}</dd>
              <dt>Firmware</dt><dd>{d.firmware}</dd>
              <dt>Inbetriebnahme</dt><dd>{dateDe(d.commissionedAt)}</dd>
            </dl></div></div>
          <div className="card"><div className="card-h"><h3>Einsatz</h3></div><div className="card-b">
            <dl className="dl">
              <dt>Kolonne</dt><dd>
                {me.role === "ADMIN" ? (
                  <>
                    <select value={d.crew} disabled={crewBusy || !crews}
                            onChange={(e) => void changeCrew(e.target.value)}
                            style={{ padding: "5px 8px", background: "var(--surface)",
                                     border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)",
                                     fontSize: 12.5, fontFamily: "var(--f-mono)" }}>
                      {crews?.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                    {crewError && <div className="err" style={{ marginTop: 8, marginBottom: 0 }}>{crewError}</div>}
                  </>
                ) : d.crew}
              </dd>
              <dt>Standort</dt><dd>{d.location}</dd>
              <dt>Einblasmeter</dt><dd>{nf(d.totalMeters)} m</dd>
              <dt>Vorgänge</dt><dd>{nf(d.runCount)}</dd>
              <dt>Ø Auslastung</dt><dd>{nf(d.metersPerDay)} m/Tag</dd>
              <dt>Letzte Übernahme</dt><dd>{d.lastSyncAt ? dateDe(d.lastSyncAt) : "—"}</dd>
            </dl></div></div>
          <div className="card"><div className="card-h"><h3>Prüfungen</h3></div><div className="card-b">
            <dl className="dl">
              <dt>Letzte Wartung</dt><dd>{d.lastServiceAt ? dateDe(d.lastServiceAt) : "—"}</dd>
              <dt>DGUV V3 / UVV</dt><dd>{dateDe(d.nextInspectionAt)}</dd>
            </dl>
            <div style={{ marginTop: 14 }}>
              <Pill status={d.daysToInspection < 0 ? "CRITICAL" : d.daysToInspection <= 30 ? "WARNING" : "OK"}>
                {d.daysToInspection < 0 ? `${Math.abs(d.daysToInspection)} Tage überfällig` : "Prüfplakette gültig"}
              </Pill>
            </div>
          </div></div>
        </div>
      )}
    </Page>
  );
}
